import { Controller } from "@hotwired/stimulus"
import "driver.js/dist/driver.css"
import { seenTours, getTutorialState, markTourDone } from "../utils/tour_persistence"

export default class extends Controller {
  static targets = ["button", "status"]

  static values = {
    tourName: String,
    autoStart: { type: Boolean, default: false },
    delay: { type: Number, default: 500 }
  }

  connect() {
    this._launchId = (this._launchId || 0) + 1
    this._loadFailed = false
    this._setLoading(false)
    this.statusTarget.textContent = ""
    if (this.autoStartValue) {
      this._autoStart()
    }
  }

  disconnect() {
    if (this._timeout) {
      clearTimeout(this._timeout)
      this._timeout = null
    }
    if (this._mutationObserver) {
      this._mutationObserver.disconnect()
      this._mutationObserver = null
    }
    // If a tour was active when navigating away, mark it as skipped
    if (this._activeTourName && !seenTours.has(this._activeTourName)) {
      markTourDone(this._activeTourName, "skip")
    }
    this.stop()
  }

  // Clear Driver's state before Turbo caches or replaces the page.
  stop() {
    this._launchId += 1
    clearTimeout(this._timeout)
    this._mutationObserver?.disconnect()
    this._driver?.destroy()
    this._driver = null
    this._setLoading(false)
  }

  _setLoading(loading) {
    this.buttonTarget.disabled = loading
    this.buttonTarget.setAttribute("aria-busy", String(loading))
    this.buttonTarget.textContent = loading ? "Loading tour…" : (this._loadFailed ? "Reload page" : "Take tour")
  }

  // Public: start a tour by name (called from topbar help menu or inline buttons)
  start(event) {
    if (this._loadFailed) {
      window.location.reload()
      return
    }
    const tourName = event?.params?.tourName || this.tourNameValue
    if (!tourName) return
    event?.preventDefault()
    this._launchTour(tourName)
  }

  _autoStart() {
    const tourName = this.tourNameValue
    if (!tourName) return

    if (seenTours.has(tourName)) return

    const state = getTutorialState()
    if (state[tourName]) return

    if (this.element.offsetParent === null && this.element.closest(".hidden")) {
      this._waitForVisible(tourName)
      return
    }

    this._scheduleStart(tourName)
  }

  _waitForVisible(tourName) {
    const hiddenAncestor = this.element.closest(".hidden")
    if (!hiddenAncestor) {
      this._scheduleStart(tourName)
      return
    }

    this._mutationObserver = new MutationObserver(() => {
      if (!hiddenAncestor.classList.contains("hidden")) {
        this._mutationObserver.disconnect()
        this._mutationObserver = null
        this._scheduleStart(tourName)
      }
    })

    this._mutationObserver.observe(hiddenAncestor, {
      attributes: true,
      attributeFilter: ["class"]
    })
  }

  _scheduleStart(tourName) {
    this._timeout = setTimeout(() => this._launchTour(tourName), this.delayValue)
  }

  async _launchTour(tourName) {
    if (this.buttonTarget.disabled || this._driver) return
    const launchId = ++this._launchId
    this.statusTarget.textContent = ""
    this._setLoading(true)

    try {
      const [{ driver }, { default: tourDefinitions }] = await Promise.all([
        import("driver.js"),
        import("../configs/tour_definitions")
      ])
      if (launchId !== this._launchId || !this.element.isConnected) return
      if (document.querySelector(".driver-active-element")) return

      const definition = tourDefinitions[tourName]
      if (!definition) return
      this._setLoading(false)
      this._showTour(driver, definition, tourName)
    } catch {
      if (launchId !== this._launchId || !this.element.isConnected) return
      this._driver?.destroy()
      this._loadFailed = true
      this.statusTarget.textContent = "The tour couldn't load. Reload the page and try again."
    } finally {
      if (launchId === this._launchId && this.element.isConnected) {
        this._setLoading(false)
        if (this._loadFailed) this.buttonTarget.focus({ preventScroll: true })
      }
    }
  }

  _showTour(driver, definition, tourName) {
    const steps = definition.steps.map(step => {
      if (!step.element) return step

      const element = step.tab ? document.querySelector(step.element) : this._visibleElementFor(step.element)
      if (!element) return null

      return {
        ...step,
        element,
        onHighlightStarted: step.tab ? () => this._selectTourTab(step.tab) : undefined
      }
    }).filter(Boolean)

    if (steps.length === 0) {
      this.statusTarget.textContent = "There are no tour steps for this view. Try another demo page."
      return
    }

    this._activeTourName = tourName

    let destroyStatus = null

    const driverInstance = driver({
      showProgress: true,
      animate: false,
      allowClose: true,
      overlayColor: "rgba(0, 0, 0, 0.5)",
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: "astrolabe-tour-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      progressText: "Step {{current}} of {{total}}",
      onPopoverRender: (popover) => {
        popover.closeButton.setAttribute("aria-label", "Close tour")
        popover.description.tabIndex = 0
      },
      steps: steps,
      onNextClick: () => {
        if (driverInstance.isLastStep()) {
          destroyStatus = "complete"
          markTourDone(tourName, "complete")
          this._activeTourName = null
          driverInstance.destroy()
          return
        }
        driverInstance.moveNext()
      },
      onCloseClick: () => {
        destroyStatus = driverInstance.isLastStep() ? "complete" : "skip"
        if (!seenTours.has(tourName)) {
          markTourDone(tourName, destroyStatus)
        }
        this._activeTourName = null
        driverInstance.destroy()
      },
      onDestroyed: () => {
        this._driver = null
        if (!seenTours.has(tourName)) {
          const status = destroyStatus || (driverInstance.isLastStep() ? "complete" : "skip")
          markTourDone(tourName, status)
        }
        this._activeTourName = null
        if (this.element.isConnected) this.buttonTarget.focus({ preventScroll: true })
      }
    })

    this._driver = driverInstance
    driverInstance.drive()
  }

  _visibleElementFor(selector) {
    return Array.from(document.querySelectorAll(selector)).find(element => this._elementIsVisible(element))
  }

  _selectTourTab(name) {
    document.querySelector(`[role="tab"][id$="-tab-${name}"]`)?.click()
  }

  _elementIsVisible(element) {
    if (element.hidden) return false

    const style = window.getComputedStyle(element)
    if (style.display === "none" || style.visibility === "hidden") return false

    return element.getClientRects().length > 0
  }
}
